/*
    Copyright (C) Paul B. Lucas 2025, 2026  ALL RIGHTS RESERVED
    Licensed to the University of Illinois Urbana Champaign

    This is a Javascript composable that uses the Loan API on the 'archibus' endpoint to proxy
    request thorugh to the ArchiBus Azure API Gateway endpoint to fetch general table 
    properties for buildings/floors/rooms adn employees.

    Employees is a June 2025 addition

    The proxy takes the full query string, minus the 'endpoint=archibus' statement from the rewrite and 
    appends that to the endpoint as the request query string to ArchiBus.  This proxy method avoids certain
    CORS issues arising from browsers interacting with the Azure API Gateway. (issues to be resolved)
    
   Included methods are:

        fetchBuildingList       -   Returns array of building names and numbers for use in selectors
        fetchRoomList           -   Returns list of all rooms (of specific use allocation) in a specified 
                                    building for use in room selectors
        fetchRoomProperties     -   Returns specific properties of a room (usable area in sq. ft.) give building
                                    id, floor, and room id.
        fetchCollegeList
        fetchDepartmentList     -   Returns list of colleges, departments, and divisions with names and codes
        fetchDivisionList

        fetchEmplDataOnLegalName -  Returns employee related data on FullLegalName (last,first MI) as found
                                    in CUSTODIAN fields on FABWEB and FABWEB PLUS

        fetechEMEmployeesOnOrgs  -  Returns all employee records on dp_id (array aOrgs)

        fetchCollegeDepartmentList      -   Returns lists of department in specified college
        fetch DepartmentDivisionList    -   Returns list of divisions (orgs) in specified department

        makeUIUCCodes       -   Makes code list for use in affiliation selects

        checkArchiBusAPIAvailability - returns true | false if API is functional
                                    

    The fetchs are async functions that awaits a results of an axios call to the ArchiBus API gateway. When promise 
    is resolved, the results are returned in agreements ref.

    NOTE:  If there is a network error or other error where ArchiBus API fails to return data, certain functions,
    e.g., fetchBuildingList and the code fetches will return a default list which is stored as a json file on 
    the /configuration directory:



    API configuration data is stored as JSON file in src/configuration/ and imported as a JSON array.  Imports
    can do this from a basic JSON file.  Parameters in the config array include the base-url, 
    endpoints, and APIM authentication data for the axios headers (API key).  Details:

        {
            "apim-key-name":    "Bearer",
            "apim-key":         "--- some key ---",
            "base-url":         "https://api.elam.fs.illinois.edu/archibus",
            "building-table":   "?table=bl",
            "room-table":       "?table=rm",
            ... etc.
        }     
        
    If the API URL changes in DNS, this file must be modified.

    <Revision History>
        02/25/2025      pblucas     created 
        03/16/2026      pblucas     Corrected typos & updated copyright
      
       
    </Revision History>
*/

import { ref } from 'vue'
import axios from 'axios'
import amApiConfigs         from '@/configuration/amAPI.config.json'
import uiucCodes            from '@/configuration/uiuc.codes.json'
import uiucBuildings        from '@/configuration/uiuc.buildings.json'
import uiucExcissions       from '@/configuration/uiuc.excised-buildings.json'

const useArchiBusAPI = () =>    {

    // edit this to false for production
    const IncludeTestIdentities = ref(true)

    // set specific endpoints and query string components from configs
    const baseURL                   =   amApiConfigs['base-url']+amApiConfigs['archibus-path']
    const URIBuildingTable          =   amApiConfigs['building-table']
    const URIRoomTable              =   amApiConfigs['room-table']
    const URIEmplTable              =   amApiConfigs['empl-table']
    const URICollegeTable           =   amApiConfigs['college-table']
    const URIDeptTable              =   amApiConfigs['dept-table']
    const URIDivTable               =   amApiConfigs['div-table']
    const QS_AllFields              =   amApiConfigs['sw-all-fields']
    const QS_ExtraBlFields          =   amApiConfigs['sw-extra-fields-bl']  
    const QS_ExtraRmFields          =   amApiConfigs['sw-extra-fields-rm']  
    const QS_ExtraRmFieldsList      =   amApiConfigs['sw-extra-fields-rm-list']
    const QS_ExtraBuFields          =   amApiConfigs['sw-extra-fields-bu']  
    const QS_ExtraDvFields          =   amApiConfigs['sw-extra-fields-dv']  
    const QS_ExtraDpFields          =   amApiConfigs['sw-extra-fields-dp']  
    const QS_ExtraEmFields          =   amApiConfigs['sw-extra-fields-em']

    const QS_RoomSelectRm           =   amApiConfigs['sw-where-room-rm-select'] 
    const QS_RoomSelectBl           =   amApiConfigs['sw-where-room-bl-select']
    const QS_CodeSelectBu           =   amApiConfigs['sw-where-bu-select']
    const QS_CodeSelectDv           =   amApiConfigs['sw-where-dv-select']
    const QS_NameSelectEm           =   amApiConfigs['sw-where-em-name-select']
    const QS_OrgSelectEm            =   amApiConfigs['sw-where-em-dpid-select']
    const QS_NetIDSelectEM          =   amApiConfigs['sw-where-em-netid-select']

    const QS_Trim                   =   amApiConfigs['sw-trim-fields']
    const QS_TestAvail              =   amApiConfigs['sw-where-0001']
    const QS_Limit                  =   amApiConfigs['sw-limit-5']
     
    // result reference to return properties: array of objects
    const buildingResults       =   ref([]) 
    const roomResults           =   ref([])   
    const collegeResults        =   ref([])  
    const departmentResults     =   ref([]) 
    const divisionResults       =   ref([])  
    const uiucCodeResults       =   ref({}) 
    const employeeEMResults     =   ref([])
    const mappedEMEmplTemplate  =   ref({})
    
    const excissions    =   ref(uiucExcissions['excissions'])

    const fetchEmplDataOnLegalName = async (legalName) =>    {

        let url =  baseURL + URIEmplTable + QS_AllFields + QS_ExtraEmFields + QS_Trim + QS_NameSelectEm + "'" +legalName+ "'"
        await axios({
            method:         'get',
            url:            url,
            headers:         {Authorization: `${amApiConfigs['apim-key-name']} ${amApiConfigs['apim-key']}`},
            withCredentials: true
        })
        .then ( (response) => {   
            if(response.data.results.length > 0) {
                employeeEMResults.value = response.data.results[0]
            }
            else {
                employeeEMResults.value = [] 
            }        
        })
        .catch( error =>  {
            console.log("FetchEmplDataOnLegalName:: "+ error.message)
            employeeEMResults.value = []
       })  
    }


    const fetchEMEmployeeOnNetid = async( netid) =>  {

 
        let url =  baseURL + URIEmplTable + QS_AllFields + QS_ExtraEmFields + QS_Trim + QS_NetIDSelectEM + "'" +netid+ "'"

        await axios({
            method:         'get',
            url:            url,
            headers:         {Authorization: `${amApiConfigs['apim-key-name']} ${amApiConfigs['apim-key']}`},
            withCredentials: true
        })
        .then ( (response) => {   
            if(response.data.results.length > 0) {
                employeeEMResults.value = response.data.results[0]
            }
            else {
                employeeEMResults.value = [] 
            }        
        })
        .catch( error =>  {
            console.log("FetchEmplDataOnNetid:: "+ error.message)
            employeeEMResults.value = []
       })  
    }


    const fetchEMEmployeesOnOrgs = async(aOrgs) =>  {
           
        let employees = []
        
        let resolvedPromises = await Promise.allSettled(aOrgs.map(org => {


            let url =  baseURL + URIEmplTable + QS_AllFields + QS_ExtraEmFields + QS_Trim + QS_OrgSelectEm + "'" + org + "'"
            let data = axios({
                method:         'get',
                url:            url,
                headers:        {Authorization: `${amApiConfigs['apim-key-name']} ${amApiConfigs['apim-key']}`}
            })
            return data;
            }
        ))
        // have an array of settled promised with either resolve or response callbacks
        resolvedPromises.forEach((rp, num)  =>  {
            if(rp.status == 'rejected') {
                //throw Error ("Axios promise rejected: fetching orgcode: "+aOrgs[num])
            }
            else if(rp.status == 'fulfilled')  {
                // data available on promise
                const data = rp.value.data      
                //console.log("Returned promise data: "+JSON.stringify(data))

                // completed and valid query returns
                for (let k in data.results)  {
                    
                    // get employee object from API call returned results[]
                    let obj = data.results[k]

                    // create the composite object - was deconstruted for storage
                    //obj['office-building'] =    {
                    //    'name':     obj['office-building-name'],
                    //    'number':   obj['office-building-number']
                    //}
                    employees.push(obj)
                }
                // DEBUG
                //console.log("useEmployeesAPI: fetchEMEmployeesOnOrgs: employees: "+JSON.stringify(employees))
                employeeEMResults.value = employees
            }
        })
    }

    const checkArchiBusAPIAvailability = async () => {
        let url =  baseURL + URIBuildingTable + QS_AllFields + QS_ExtraBlFields + QS_Trim + QS_TestAvail
        await axios({
            method:         'get',
            url:            url,
            headers:         {Authorization: `${amApiConfigs['apim-key-name']} ${amApiConfigs['apim-key']}`},
            withCredentials: true
        })
        .then ( (response) => {   
            if(response.data.results.length > 0) return true;  
                   
        })
        .catch( error =>  {
            return false;
        })
    }
   
    /*
    Function:  Fetch ArchiBus BL table (buildings)
    Filters on status codes:
        A=Active                leave in list
        R=Razed (Demolished)    remove from list
        P=Planned               remove from list
        I=Inactive              remove from list
        E=Erroneous Entry       remove from list

    And pulls uiuc.excised-buildings.json for list of buildings to be suppressed.
    then filters on these excissions.

    */
    const  fetchBuildingList = async ()  =>  {

        let url =  baseURL + URIBuildingTable + QS_AllFields + QS_ExtraBlFields + QS_Trim
        await axios({
            method:         'get',
            url:            url,
            headers:        {Authorization: `${amApiConfigs['apim-key-name']} ${amApiConfigs['apim-key']}`},
            withCredentials: true
        })
        .then ( (response) => {   
            let objectList = []
            response.data.results.forEach( (b) => {
                let obj     =   {}
                obj.name    =   b.name
                obj.number  =   b.bl_id   
                obj.status  =   b.bl_status   
                objectList.push(obj)
            })
            return objectList;
        })
        .then( (objectList) =>  {
            // sort list
            objectList.sort((a, b) => {
                if (a.name < b.name) {
                    return -1;
                }
                if (a.name > b.name) {
                    return 1;
                }
                // codes must be equal
                return 0;
            });
            return objectList;
        })
        .then( (objectList) =>  {
            // run a list of filters to remove buildings not likely to be used as office 
            // only active 'A' buildings - status 'R', 'E', "p", ,and 'I' removed
            return objectList.filter(o  =>  o.status.includes('A'));
        })
        .then( (objectList) =>  {
            // remove buildings on specific excission list
            for (let i=0; i < excissions.value.length; i++)   {
                objectList = objectList.filter( o => !o.name.includes(excissions.value[i]))
                /*
                for (let j=0; j < objectList.length; j++) {
                    if(objectList[j].name.includes(excissions.value[i])) {
                        console.log("useArchiBusAPI;: fetchBuildingList: removing "+JSON.stringify(objectList[j]))
                        //objectList = objectList.slice(0,j).concat(objectList.slice(-j))
                    }
                }  
                */  
            }
            return objectList;
        })
        .then( (objectList) =>  {
            buildingResults.value      =   objectList  
        })
        .catch( error =>  {
            console.log("FetchBuildingList:: "+ error.message+ " - using defaults")
            buildingResults.value = uiucBuildings['buildings']
        })
       
    }   // end function

    const  fetchRoomProperties = async (sBldgId, sRoomId)  =>  {

        let url =  baseURL + URIRoomTable + QS_AllFields + QS_ExtraRmFields 
                 + QS_Trim + QS_RoomSelectBl +  "%27"+sBldgId+"%27" + QS_RoomSelectRm +  "%27"+sRoomId+"%27"
        await axios({
            method:         'get',
            url:            url,
            headers:        {Authorization: `${amApiConfigs['apim-key-name']} ${amApiConfigs['apim-key']}`},
            withCredentials: true
        })
        .then ( (response) => {   
            roomResults.value      =   response.data.results     // array of results        
        })
        .catch( error =>  {
            console.log("FetchRoomProperties:: "+ error.message)
            roomResults.value = []
        })
       
    }   // end function

    /*
    Function: Fetched rooms in a building.
    Usage:  Will be filtered on type code.
    Type code 'null' indicates that building use is split and need to look at rm_pct table.
    */
    const  fetchRoomList = async (building_number)  =>  {

        let url =  baseURL + URIRoomTable + QS_AllFields + QS_ExtraRmFieldsList 
                 + QS_Trim + QS_RoomSelectBl + "'" +building_number + "'"
        await axios({
            method:         'get',
            url:            url,
            headers:        {Authorization: `${amApiConfigs['apim-key-name']} ${amApiConfigs['apim-key']}`}
        })
        .then ( (response) => {   
            roomResults.value      =   response.data.results             
        })
        .catch( error =>  {
            console.log("FetchRoomList:: "+ error.message)
            roomResults.value = []
        })
       
    }   // end function

    // MARK:  The following three functions returns the SOR affiliation lists for colleges,
    //        departments, and divisions (orgcodes) 
    //

    // FUNCTION:  Obtain the list of college names and codes
    // Arguments: none
    // Returns: array
    //      [{ "name": "<college name>","bu_id (college code)":"<code>"},...]
    //

    /*
               transformRequest:[ (data, headers) =>  {
                // Do whatever you want to transform the data
                console.log('fetchCollegeList:: Axios Request Headers: '+JSON.stringify(headers))
                return data
                }]
    */

    const  fetchCollegeList = async ()  =>  {

         let url =  baseURL + URICollegeTable + QS_AllFields + QS_ExtraBuFields 
                 + QS_Trim   

        await axios({
            method:         'get',
            url:            url,
            headers:         {Authorization: `${amApiConfigs['apim-key-name']} ${amApiConfigs['apim-key']}`}
        })
        .then ( (response) => {  
            //console.log("fetchCollegeList:: response: "+JSON.stringify(response.data.results)) 
            let codeObj = []
            response.data.results.forEach( (o)  => {
                let oo = {}
                oo['name']  =   o['name']
                oo['code']  =   o['bu_id']
                oo['chart'] =   o['chart_id']
                codeObj.push(oo)
            })
            // report results as array of objects
            collegeResults.value = codeObj            
        })
        .catch( error =>  {
            console.log("useArchiBusAPI:: FetchCollegeList:: - using json file: "+ error.message)
            // return codes from backup json config list
            collegeResults.value = uiucCodes['college-codes']
        })
       
    }   // end function

    // FUNCTION:  Obtain the list of all departments 
    // Arguments: none
    // Returns: array
    //      [{ "name": "<division name>","bu_id (college code)":"<code>", "dv_id (deptcode)":"<dept code>" },...]
    //
    const  fetchDepartmentList = async ()  =>  {

        let url =  baseURL + URIDeptTable + QS_AllFields + QS_ExtraDvFields 
                 + QS_Trim  

        await axios({
            method:         'get',
            url:            url,
            headers:        {Authorization: `${amApiConfigs['apim-key-name']} ${amApiConfigs['apim-key']}`},
            withCredentials: true
        })
        .then ( (response) => {   
            let codeObj = []
            response.data.results.forEach( (o)  => {
                let oo = {}
                oo['name']          =   o['name']
                oo['college-code']  =   o['bu_id']
                oo['dept-code']     =   o['dv_id']
                codeObj.push(oo) 

                // MARK: - DEVELOPER 
                // hack to add in test identity affiliations (S.H.I.E.L.D)
                // remove for production
                if((o['bu_id'] == 'NT') && IncludeTestIdentities.value)  {
                    let ooHack = {}
                    ooHack['name']          =   "S.H.I.E.L.D"
                    ooHack['college-code']  =   'NT'
                    ooHack['dept-code']     =   'NT1'
                    codeObj.push(ooHack)
                }
            })   
            departmentResults.value = codeObj         
        })
        .catch( error =>  {
            console.log("useArchiBusAPI:: FetchDepartmentList:: - using json file: "+ error.message)
            // return codes from backup json config list
            departmentResults.value = uiucCodes['dept-codes']
        })
       
    }   // end function
    
        // FUNCTION:  Obtain the list of departments within a college 
    // Arguments: college code
    // Returns: array
    //      [{ "name": "<division name>","bu_id (college code)":"<code>", "dv_id (deptcode)":"<dept code>" },...]
    //
    const  fetchCollegeDepartmentList = async (cc)  =>  {

        let url =  baseURL + URIDeptTable + QS_AllFields + QS_ExtraDvFields 
                 + QS_Trim + QS_CodeSelectBu + "'" + cc + "'"  

        await axios({
            method:         'get',
            url:            url,
            headers:        {Authorization: `${amApiConfigs['apim-key-name']} ${amApiConfigs['apim-key']}`},
            withCredentials: true
        })
        .then ( (response) => {   
            let codeObj = []
            response.data.results.forEach( (o)  => {
                let oo = {}
                oo['name']          =   o['name']
                oo['college-code']  =   o['bu_id']
                oo['dept-code']     =   o['dv_id']
                codeObj.push(oo) 
            })   
            // MARK: - DEVELOPER 
            // hack to add in test identity affiliations (S.H.I.E.L.D)
            // remove for production
            if( (cc == 'NT') && IncludeTestIdentities.value)  {
                let ooHack = {}
                ooHack['name']          =   "S.H.I.E.L.D"
                ooHack['college-code']  =   'NT'
                ooHack['dept-code']     =   'NT1'
                codeObj.push(ooHack)
            }
            

            // final return
            departmentResults.value = codeObj  
        })
        .catch( error =>  {
            console.log("FetchDepartmentList:: "+ error.message)
            departmentResults.value = []
        })
       
    }   // end function
 
    // FUNCTION:  Obtain the list of all division (org) codes
    // Arguments: none
    // Returns: array
    //      [{ "name": "<division name>","dv_id (department code)":"<code>", "dp_id (orgcode)":"<orgcode>"},...]
    //
    const  fetchDivisionList = async ()  =>  {

        let url =  baseURL + URIDivTable + QS_AllFields + QS_ExtraDpFields 
                 + QS_Trim 
                 
        await axios({
            method:         'get',
            url:            url,
            headers:        {Authorization: `${amApiConfigs['apim-key-name']} ${amApiConfigs['apim-key']}`},
            withCredentials: true
        })
        .then ( (response) => {   
            let codeObj = []
            response.data.results.forEach( (o)  => {
                let oo = {}
                oo['name']          =   o['name']
                oo['dept-code']     =   o['dv_id']
                oo['org']           =   o['dp_id']
                codeObj.push(oo) 
            })          
            // MARK: - DEVELOPER 
            // hack to add in test identity affiliations (S.H.I.E.L.D)
            // remove for production
            // Dept Code NT1 for SHIELD will not be present so add at end
            if( IncludeTestIdentities.value )  {
                let ooHack = {}
                ooHack['name']          =   "Avengers"  
                ooHack['dept-code']     =   'NT1'
                ooHack['org']           =   '619'
                codeObj.push(ooHack)
            }
            divisionResults.value = codeObj            
        })
        .catch( error =>  {
            console.log("useArchiBusAPI:: FetchDivisionList:: - using json file: "+ error.message)
            // return codes from backup json config list
            divisionResults.value = uiucCodes['org-codes']
        })    
    }   // end function
    
    // FUNCTION:  Obtain the list of division (org) codes
    // Arguments: dv_id
    // Returns: array
    //      [{ "name": "<division name>","dv_id (department code)":"<code>", "dp_id (orgcode)":"<orgcode>"},...]
    //
    const  fetchDepartmentDivisionList = async (dv)  =>  {

        let url =  baseURL + URIDivTable + QS_AllFields + QS_ExtraDpFields 
                 + QS_Trim +  QS_CodeSelectDv + "'" + dv + "'"
                 
        await axios({
            method:         'get',
            url:            url,
            headers:        {Authorization: `${amApiConfigs['apim-key-name']} ${amApiConfigs['apim-key']}`},
            withCredentials: true
        })
        .then ( (response) => {   
            let codeObj = []
            response.data.results.forEach( (o)  => {
                let oo = {}
                oo['name']          =   o['name']
                oo['dept-code']     =   o['dv_id']
                oo['org']           =   o['dp_id']
                codeObj.push(oo) 
            })   
            // MARK: - DEVELOPER 
            // hack to add in test identity affiliations (S.H.I.E.L.D)
            // remove for production
            if( (dv == 'NT1')  && IncludeTestIdentities.value)  {
                let ooHack = {}
                ooHack['name']          =   "Avengers"  
                ooHack['dept-code']     =   'NT1'
                ooHack['org']           =   '619'
                codeObj.push(ooHack)
            }
            divisionResults.value = codeObj         
        })
        .catch( error =>  {
            console.log("FetchDivisionList:: "+ error.message)
            divisionResults.value = []
        })
       
    }   // end function
    
    // MARK: The following function performs calls to ArchiBus to obtain the college, department, and division
    //       lists, and remaps them into the format used for the uiuc.codes.json file.  Use of this function allows
    //       the configuration file to be deprecated and removes any maintenance activities needed when affiliation
    //       changes are made at the campus.
    //
    //       The queries are made on department and division without regard to parent, e.g., no where clauses.
    //
    //  Function: Makes a json uiuc code list object
    //  Arguments: none
    //  Returns: mapped codes list (3 segments) per configuration file.
    //
    //  Usage: call function then set constant to function results value (ref)
    //  e.g.,   const uiucCodes = ref({})
    //          makeUIUCCodes()
    //          .then( () = {
    //              uiucCodes.value = uiucCodeResults.value      
    //          })
    //
    //  In the event that the ArchiBus API is not functional (such as until Rick fixes the CORS headers)
    //  the code list is obtained from a json config file.
    //
    const makeUIUCCodes = async () =>   {

        return new Promise((resolve, _) => {

            // initialize return object
            let codeObj = {}
            codeObj['meta']  = {}

            fetchCollegeList()
            .then( () => {
                codeObj['college-codes'] = collegeResults.value
                fetchDepartmentList()
                .then( () => {
                    codeObj['dept-codes'] = departmentResults.value 
    
                    fetchDivisionList()
                    .then( () => {    
                        codeObj['org-codes'] = divisionResults.value
    
                        // add meta data - ISO 8601
                        let date = new Date()
                        codeObj.meta['version']         =   "1.0.0"
                        codeObj.meta['generation-date'] =   date.toString()
                        codeObj.meta['ref-source']      =   "ArchiBus API"
    
                        // final update
                        uiucCodeResults.value = codeObj
                        resolve(uiucCodeResults.value)   
                    })
                })
            })     
        })   
    }

    // FUNCITON:  Parses employee pulled from ArchiBUs EM table and populates
    // the employee record, returning the record.
    //            Not all properties are availale. 
    //            Admins must fill in missing data on employee record
    // Arguments: claims    Claims object from authentication
    //
    // Returns:   emplTemplate  employee object with all claims and EM data added  
    //            sets mappedEmplTemplate ref

    const mapTemplatedEMEmployee = async (claims) =>    {
  
        //console.log("useArchiBusAPI:: mapTemplatedEMEmployee: claims: "+JSON.stringify(claims))

        // generate netid from 'username' in claims
        let netid = claims.preferred_username.split("@")[0]
        let college = (claims.hasOwnProperty('department'))?claims.department:""
        let uiucCodes       =   []
        let aCollegeCodes   =   []
        let aDeptCodes      =   []             
        let aOrgCodes       =   []  

        let templatedEmployee = {
            'name-concat':            "",
            'first-name':             "",
            'last-name':              "",
            'middle-initial':         "",
            'netid':                  netid,
            'ms-object-id':           claims.oid,
            'uin':                    "",
            'home-org':               "",
            'eclass-code':            "",
            'title':                  claims.jobtitle,
            'address1':               "",
            "address2":               "",
            "city":                   "",
            "state":                  "",
            "postcode":               "",
            "country":                "United States",
            "mailcode":               "",
            "timezone":               "America/Chicago",        // default cst/cdt
            "req-photo-suppress":     false,
            "college":                college,                  // college returned as department on claims
            "college-code":           null,
            "department":             null,
            "department-code":        null,
            "division":               null,
            'division-code':          null,
    
     
            // the following user properties will be updated by membership in Entra security groups
            // these are mutated on store.state.user AFTER user is update with new Employee
            'is-sys-admin':           false,
            'is-dept-admin':          false,
            // additional security constraints
            // must be added by admin on employee record
            'admins-multiple-orgs':   false,
            'allowed-orgs':           [],  
            // pull known contacts from claims   
            'contacts':               []
        }

        // affiliation mapping required code table
        await makeUIUCCodes()
        .then ( async () => {
            uiucCodes = uiucCodeResults.value
            // select list 
            aCollegeCodes         =   uiucCodes['college-codes']
            aDeptCodes            =   uiucCodes["dept-codes"]             
            aOrgCodes             =   uiucCodes["org-codes"]                             
            return uiucCodes;
        })

        // using netid from claims, fetch employee from Archibus EM table and map to employee model props
        await fetchEMEmployeeOnNetid(netid)
        .then( async () =>    {
            let oEm = employeeEMResults.value
            // EM table has name == fullLegalName (Last, First MI)
            let aNameParts = oEm.name.split(',')
            // test for single name - e.g. Indonesian/Malayasian                
            if (typeof aNameParts[1] != 'undefined') {
                let aFirstNameParts = aNameParts[1].trim().split(" ")
                if(typeof aFirstNameParts[1] != 'undefined' ) {
                    templatedEmployee['middle-initial']   = aFirstNameParts[1]
                }
                if(typeof aFirstNameParts[0] != 'undefined' ) {
                    // overwrite first name
                    templatedEmployee['first-name']   = aFirstNameParts[0]
                }
            }
            // set last name
            templatedEmployee['last-name'] = aNameParts[0]

            // build concat name as first MI Last
            let tempDisplayName = ""
            if(templatedEmployee['first-name'].length > 0)  {
                tempDisplayName += templatedEmployee['first-name']
                tempDisplayName += " "
            }
            if(templatedEmployee['middle-initial'].length > 0)  {
                tempDisplayName += templatedEmployee['middle-initial']
                tempDisplayName += " "               
            }
            tempDisplayName += templatedEmployee['last-name']
            templatedEmployee['name-concat']    =   tempDisplayName

            // other properties
            templatedEmployee['uin']             =   oEm['em_id']
            templatedEmployee['department-code'] =   oEm['dv_id']
            templatedEmployee['division-code']   =   oEm['dp_id']
            templatedEmployee['home-org']        =   oEm['org_id']

            // lookup the college name, college-code, and division name on uiucCodes
            let div = aOrgCodes.find( (d) => d.org == oEm['dp_id'])
            if(typeof div != 'undefined')   {
                templatedEmployee['division']        =   div.name
            }
            else console.log("useArchiBusAPI: mapTemplatedEMEmployee: listed division id (dp_id): "+oEm['dp_id']+" not found in UIUC codes")

            let dept = aDeptCodes.find( (d) => d['dept-code'] == oEm['dv_id'])
            if(typeof dept != 'undefined') {
                templatedEmployee['college-code']    =   dept['college-code']
                templatedEmployee['department']      =   dept['name']

                // resolve college name
                let col = aCollegeCodes.find( (c) => c.code == dept['college-code'])
                if(typeof col != 'undefined') {
                    templatedEmployee['college']         =   col.name
                }
                else console.log("useArchiBusAPI: mapTemplatedEMEmployee: cound not obtain college record in UIUC codes")
            }
            else console.log("useArchiBusAPI: mapTemplatedEMEmployee: listed dept id (dv_id): "+oEm['dv_id']+" not found in UIUC codes")

            // can pull email from claims so create a contact object
            let contactTemplate         =   {
                "type":     "email",
                "value":    claims.username
            }
            templatedEmployee['contacts'].push(contactTemplate)        
            
            // set return ref
            mappedEMEmplTemplate.value = templatedEmployee
            return templatedEmployee;
        })    
    }
    return {buildingResults, roomResults, collegeResults, departmentResults, divisionResults, uiucCodeResults,
            fetchBuildingList, fetchRoomProperties, fetchRoomList, fetchCollegeList, fetchDepartmentList, 
            fetchCollegeDepartmentList, fetchDivisionList, fetchDepartmentDivisionList, makeUIUCCodes,
            checkArchiBusAPIAvailability, employeeEMResults, fetchEmplDataOnLegalName, fetchEMEmployeesOnOrgs,
            fetchEMEmployeeOnNetid, mapTemplatedEMEmployee, mappedEMEmplTemplate }
}

export default useArchiBusAPI